/**
 * 🕒 セッション管理システム - 自動タイムアウト機能
 * セキュリティ強化・アクティビティ監視・グレースフルログアウト
 *
 * セキュリティ機能:
 * - 12時間非アクティブで自動ログアウト
 * - 30分前警告通知
 * - アクティビティ自動検知
 * - セッションハイジャック防止
 */

import { showNotification } from '@mantine/notifications';
import { apiClient } from '@/services/api';

// 🔧 セッション設定
const SESSION_CONFIG = {
  // タイムアウト設定
  SESSION_TIMEOUT: 12 * 60 * 60 * 1000, // 12時間
  WARNING_BEFORE_TIMEOUT: 30 * 60 * 1000, // 30分前警告
  GRACE_PERIOD: 2 * 60 * 1000, // 2分猶予期間
  
  // アクティビティ監視間隔
  ACTIVITY_CHECK_INTERVAL: 60 * 1000, // 1分ごと
  HEARTBEAT_INTERVAL: 5 * 60 * 1000, // 5分ごとのサーバー通信
  
  // 監視対象イベント
  ACTIVITY_EVENTS: [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'focus',
  ] as const,
  
  // セッション延長を無視するイベント（セキュリティ）
  IGNORED_ACTIVITIES: [
    'mousemove', // マウス移動のみは無視
  ] as const,
} as const;

// 📊 セッション状態の型定義
interface SessionState {
  lastActivity: number;
  sessionStart: number;
  warningShown: boolean;
  isActive: boolean;
  heartbeatInterval?: NodeJS.Timeout;
  activityCheckInterval?: NodeJS.Timeout;
  logoutTimeout?: NodeJS.Timeout;
  warningTimeout?: NodeJS.Timeout;
}

// 🔔 通知状態管理
interface NotificationState {
  warningId?: string;
  finalWarningId?: string;
  countdownInterval?: NodeJS.Timeout;
}

class SessionManager {
  private state: SessionState = {
    lastActivity: 0,
    sessionStart: 0,
    warningShown: false,
    isActive: false,
  };

  private notifications: NotificationState = {};
  private logoutCallback?: () => Promise<void>;
  private extensionCallback?: () => void;
  private activityListeners: Array<() => void> = [];

  /**
   * 🚀 セッション管理開始
   */
  startSession(logoutCallback?: () => Promise<void>): void {
    // 既にセッションがアクティブな場合は何もしない（重複防止）
    if (this.state.isActive) {
      return;
    }

    const now = Date.now();

    this.state = {
      ...this.state,
      sessionStart: now,
      lastActivity: now,
      isActive: true,
      warningShown: false,
    };

    this.logoutCallback = logoutCallback;

    // イベントリスナー登録
    this.setupActivityListeners();

    // 定期チェック開始
    this.startPeriodicChecks();

    // 初期化通知
    this.showSessionStartNotification();
  }

  /**
   * 🛑 セッション終了
   */
  endSession(): void {
    this.state.isActive = false;
    
    // タイマークリア
    this.clearAllTimers();
    
    // リスナー削除
    this.removeActivityListeners();
    
    // 通知クリア
    this.clearAllNotifications();
  }

  /**
   * 🔄 セッション延長（手動）
   */
  extendSession(): void {
    if (!this.state.isActive) return;

    const now = Date.now();
    this.state.lastActivity = now;
    this.state.warningShown = false;

    // 既存の警告とタイムアウトをクリア
    this.clearWarningTimeout();
    this.clearLogoutTimeout();
    this.clearAllNotifications();

    // 新しいタイマー設定
    this.scheduleWarning();
    this.scheduleLogout();

    // 延長通知（重複防止のため固定IDを使用）
    showNotification({
      id: 'session-extend-notification',
      title: 'セッション延長',
      message: 'セッションが30分延長されました',
      color: 'green',
      autoClose: 3000,
    });

    // 延長コールバック実行
    if (this.extensionCallback) {
      this.extensionCallback();
    }
  }

  /**
   * 📊 現在のセッション状態取得
   */
  getSessionInfo(): {
    isActive: boolean;
    sessionDuration: number;
    timeUntilTimeout: number;
    timeUntilWarning: number;
    lastActivity: Date;
    sessionStart: Date;
  } {
    const now = Date.now();
    const timeUntilTimeout = Math.max(0, 
      (this.state.lastActivity + SESSION_CONFIG.SESSION_TIMEOUT) - now
    );
    const timeUntilWarning = Math.max(0, 
      (this.state.lastActivity + SESSION_CONFIG.SESSION_TIMEOUT - SESSION_CONFIG.WARNING_BEFORE_TIMEOUT) - now
    );

    return {
      isActive: this.state.isActive,
      sessionDuration: now - this.state.sessionStart,
      timeUntilTimeout,
      timeUntilWarning,
      lastActivity: new Date(this.state.lastActivity),
      sessionStart: new Date(this.state.sessionStart),
    };
  }

  /**
   * 🔗 延長コールバック設定
   */
  onSessionExtension(callback: () => void): void {
    this.extensionCallback = callback;
  }

  // =======================================================================
  // 🔧 内部メソッド
  // =======================================================================

  /**
   * 👂 アクティビティリスナー設定
   */
  private setupActivityListeners(): void {
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(eventType => {
      const listener = (event: Event) => {
        // 無視対象アクティビティをチェック
        if (SESSION_CONFIG.IGNORED_ACTIVITIES.includes(eventType as any)) {
          return;
        }

        this.recordActivity(eventType);
      };

      document.addEventListener(eventType, listener, { passive: true });
      this.activityListeners.push(() => {
        document.removeEventListener(eventType, listener);
      });
    });
  }

  /**
   * 🗑️ アクティビティリスナー削除
   */
  private removeActivityListeners(): void {
    this.activityListeners.forEach(remove => remove());
    this.activityListeners = [];
  }

  /**
   * 📝 アクティビティ記録
   */
  private recordActivity(eventType: string): void {
    if (!this.state.isActive) return;

    const now = Date.now();
    const timeSinceLastActivity = now - this.state.lastActivity;

    // 短時間の連続アクティビティは無視（パフォーマンス最適化）
    if (timeSinceLastActivity < 1000) return;

    this.state.lastActivity = now;

    // 警告が表示されていた場合はクリア
    if (this.state.warningShown) {
      this.clearAllNotifications();
      this.state.warningShown = false;
    }

    // タイマー再設定
    this.rescheduleTimers();
  }

  /**
   * ⏰ 定期チェック開始
   */
  private startPeriodicChecks(): void {
    // セッション状態チェック
    this.state.activityCheckInterval = setInterval(() => {
      this.checkSessionTimeout();
    }, SESSION_CONFIG.ACTIVITY_CHECK_INTERVAL);

    // サーバーハートビート
    this.state.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, SESSION_CONFIG.HEARTBEAT_INTERVAL);
  }

  /**
   * 🔍 セッションタイムアウトチェック
   */
  private checkSessionTimeout(): void {
    if (!this.state.isActive) return;

    const now = Date.now();
    const timeSinceLastActivity = now - this.state.lastActivity;
    const timeUntilTimeout = SESSION_CONFIG.SESSION_TIMEOUT - timeSinceLastActivity;
    const timeUntilWarning = SESSION_CONFIG.SESSION_TIMEOUT - SESSION_CONFIG.WARNING_BEFORE_TIMEOUT - timeSinceLastActivity;

    // 警告表示タイミング
    if (timeUntilWarning <= 0 && !this.state.warningShown) {
      this.showTimeoutWarning();
    }

    // タイムアウト
    if (timeUntilTimeout <= 0) {
      this.executeTimeout();
    }
  }

  /**
   * ⚠️ タイムアウト警告表示
   */
  private showTimeoutWarning(): void {
    if (this.state.warningShown) return;

    this.state.warningShown = true;
    
    // 既存の警告通知をクリア（重複防止）
    this.clearAllNotifications();
    
    const remainingMinutes = Math.ceil(SESSION_CONFIG.WARNING_BEFORE_TIMEOUT / (60 * 1000));

    // 重複防止のため、IDを使用して既存通知を上書き
    this.notifications.warningId = showNotification({
      id: 'session-warning-unique',
      title: '⚠️ セッションタイムアウト警告',
      message: `${remainingMinutes}分後に自動ログアウトされます`,
      color: 'yellow',
      autoClose: false,
      withCloseButton: true,
    }) as string;

    // カウントダウン更新
    this.startCountdown();

    // 最終警告のスケジュール
    this.scheduleeFinalWarning();
  }

  /**
   * ⏰ カウントダウン開始
   */
  private startCountdown(): void {
    // 既存のカウントダウンをクリア（重複防止）
    this.clearCountdown();
    
    let remainingSeconds = SESSION_CONFIG.WARNING_BEFORE_TIMEOUT / 1000;

    this.notifications.countdownInterval = setInterval(() => {
      remainingSeconds -= 30; // 30秒間隔で更新

      if (remainingSeconds > 0 && this.state.isActive && this.state.warningShown) {
        const minutes = Math.ceil(remainingSeconds / 60);
        // 同じIDで通知を上書き（重複防止）
        this.notifications.warningId = showNotification({
          id: 'session-warning-unique',
          title: '⚠️ セッションタイムアウト警告',
          message: `${minutes}分後に自動ログアウトされます`,
          color: 'yellow',
          autoClose: false,
          withCloseButton: true,
        }) as string;
      } else {
        this.clearCountdown();
      }
    }, 30000);
  }

  /**
   * 🚨 最終警告表示
   */
  private scheduleeFinalWarning(): void {
    const finalWarningTime = SESSION_CONFIG.WARNING_BEFORE_TIMEOUT - (2 * 60 * 1000); // 2分前
    
    setTimeout(() => {
      if (!this.state.isActive || !this.state.warningShown) return;

      // 最終警告も重複防止のため固定IDを使用
      this.notifications.finalWarningId = showNotification({
        id: 'final-session-warning-unique',
        title: '🚨 最終警告',
        message: '2分後にログアウトします。継続する場合は何かアクションしてください',
        color: 'red',
        autoClose: false,
        withCloseButton: false,
      }) as string;
    }, finalWarningTime);
  }

  /**
   * ⏰ タイマー再スケジュール
   */
  private rescheduleTimers(): void {
    this.clearLogoutTimeout();
    this.clearWarningTimeout();
    
    this.scheduleWarning();
    this.scheduleLogout();
  }

  /**
   * 📅 警告スケジュール
   */
  private scheduleWarning(): void {
    const warningTime = SESSION_CONFIG.SESSION_TIMEOUT - SESSION_CONFIG.WARNING_BEFORE_TIMEOUT;
    
    this.state.warningTimeout = setTimeout(() => {
      this.showTimeoutWarning();
    }, warningTime);
  }

  /**
   * 📅 ログアウトスケジュール
   */
  private scheduleLogout(): void {
    this.state.logoutTimeout = setTimeout(() => {
      this.executeTimeout();
    }, SESSION_CONFIG.SESSION_TIMEOUT);
  }

  /**
   * 🔄 サーバーハートビート
   */
  private async sendHeartbeat(): Promise<void> {
    if (!this.state.isActive) return;

    try {
      // セッション状態をサーバーに送信（v3 API使用）
      await apiClient.post('/auth/heartbeat', {
        timestamp: Date.now(),
        sessionId: sessionStorage.getItem('session_id'),
      });

    } catch (error) {
      console.warn('Heartbeat error:', error);
    }
  }

  /**
   * ⏰ タイムアウト実行
   */
  private async executeTimeout(): Promise<void> {
    if (!this.state.isActive) return;

    // 既存の警告通知をすべてクリア
    this.clearAllNotifications();

    // 最終通知（重複防止のため固定IDを使用）
    showNotification({
      id: 'session-timeout-final',
      title: 'セッションタイムアウト',
      message: '非アクティブのためログアウトします',
      color: 'red',
      autoClose: 3000,
    });

    // セッション終了
    this.endSession();

    // ログアウト実行
    if (this.logoutCallback) {
      try {
        await this.logoutCallback();
      } catch (error) {
        console.error('Auto logout failed:', error);
        // フォールバック: 強制的にログイン画面へ
        window.location.replace('/login');
      }
    } else {
      window.location.replace('/login');
    }
  }

  /**
   * 🧹 通知クリア
   */
  private clearAllNotifications(): void {
    // Individual notifications are handled by Mantine
    // Here we clear our tracking
    this.notifications = {};
    this.clearCountdown();
  }

  /**
   * 🔄 カウントダウンクリア
   */
  private clearCountdown(): void {
    if (this.notifications.countdownInterval) {
      clearInterval(this.notifications.countdownInterval);
      this.notifications.countdownInterval = undefined;
    }
  }

  /**
   * ⏰ すべてのタイマークリア
   */
  private clearAllTimers(): void {
    this.clearLogoutTimeout();
    this.clearWarningTimeout();
    this.clearCountdown();

    if (this.state.activityCheckInterval) {
      clearInterval(this.state.activityCheckInterval);
      this.state.activityCheckInterval = undefined;
    }

    if (this.state.heartbeatInterval) {
      clearInterval(this.state.heartbeatInterval);
      this.state.heartbeatInterval = undefined;
    }
  }

  private clearLogoutTimeout(): void {
    if (this.state.logoutTimeout) {
      clearTimeout(this.state.logoutTimeout);
      this.state.logoutTimeout = undefined;
    }
  }

  private clearWarningTimeout(): void {
    if (this.state.warningTimeout) {
      clearTimeout(this.state.warningTimeout);
      this.state.warningTimeout = undefined;
    }
  }

  /**
   * 🎉 セッション開始通知
   */
  private showSessionStartNotification(): void {
    // セッション開始通知は無効化（AuthContextで既にログイン成功通知を表示しているため）
    // showNotification({
    //   title: 'セッション開始',
    //   message: '30分後に自動ログアウトされます',
    //   color: 'blue',
    //   autoClose: 5000,
    // });
  }
}

// 🌐 シングルトンインスタンス
export const sessionManager = new SessionManager();

// 🔧 ユーティリティ関数
export const startSession = (logoutCallback?: () => Promise<void>) => 
  sessionManager.startSession(logoutCallback);

export const endSession = () => sessionManager.endSession();
export const extendSession = () => sessionManager.extendSession();
export const getSessionInfo = () => sessionManager.getSessionInfo();

export const onSessionExtension = (callback: () => void) => 
  sessionManager.onSessionExtension(callback);

// Default export
export default sessionManager;